import { injectable, inject } from 'inversify';
import { SearchProductsUseCase } from '../../../domain/usecases/ProductUseCases';
import type { ProductRepository } from '../../../domain/repositories/ProductRepository';
import { Product } from '../../../domain/models';
import { TYPES } from '../../../di/types';

/**
 * Implementation of SearchProductsUseCase
 * This contains the business logic for searching products
 */
@injectable()
export class SearchProductsUseCaseImpl implements SearchProductsUseCase {
  constructor(
    @inject(TYPES.ProductRepository) private productRepository: ProductRepository
  ) {}

  async execute(query: string, branchId: string): Promise<Product[]> {
    try {
      // Validate input parameters
      this.validateSearchParameters(query, branchId);

      // Clean and prepare search query
      const cleanQuery = this.cleanSearchQuery(query);

      // Perform search through repository
      const products = await this.productRepository.search(cleanQuery, branchId);

      // Apply business rules for search results
      return this.filterSearchResults(products);
    } catch (error) {
      console.error('Product search failed:', error);
      throw error;
    }
  }

  private validateSearchParameters(query: string, branchId: string): void {
    if (!query || query.trim().length === 0) {
      throw new Error('Search query is required');
    }

    if (query.trim().length < 2) {
      throw new Error('Search query must be at least 2 characters long');
    }

    if (!branchId || branchId.trim().length === 0) {
      throw new Error('Branch ID is required');
    }
  }

  private cleanSearchQuery(query: string): string {
    // Remove extra whitespace and convert to lowercase for better matching
    return query.trim().toLowerCase();
  }

  private filterSearchResults(products: Product[]): Product[] {
    // Apply business rules for filtering search results
    return products.filter(product => {
      // Only return active products
      if (!product.isActive) {
        return false;
      }

      // Note: Stock checking moved to BranchInventoryRepository  
      // if (product.stock <= 0) {
      //   return false;
      // }

      return true;
    });
  }
} 